"use client";

import React, { useEffect, useState, useRef, useCallback, useMemo, Suspense } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import Navbar from "@/components/Navbar";
import { useRouter, useSearchParams } from 'next/navigation';
import ImageCarousel from "@/components/ImageCarousel";
import Title from "@/components/Title";
import FilterBar from "@/components/FilterBar";
import { listProducts, getProductTypes, getActivePromotions } from "@/lib/data/products";
import SmoothCursorFollower from "@/components/SmoothCursor";
import Footer from "@/components/Footer";
import ProductGrid from "@/components/ProductGrid";
import ShopView from "@/components/ShopView";
import ErrorBoundary from '@/components/ErrorBoundary';
import { useTranslation } from '@/lib/i18n/useTranslation';

// Curated display order for the portfolio tabs. Any product Typ that exists in
// Medusa but isn't listed here is appended alphabetically, so new kunst types
// show up automatically. The shop + urnen entries are always appended after.
const TYPE_ORDER = ['Objekte', 'Zeichnungen', 'Schalen', 'Schmuck'];

// Types that belong to the urnen shop, not the kunst portfolio. /store/product-types
// is not scoped to a sales channel, so we filter these out — the urnen shop is
// reached via its own external link in the filterbar.
const EXCLUDE_TYPES = new Set(['Urne']);

const slugify = (value) =>
    String(value)
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

function orderTypes(values) {
    const filtered = values.filter((v) => !EXCLUDE_TYPES.has(v));
    const known = TYPE_ORDER.filter((v) => filtered.includes(v));
    const extra = filtered
        .filter((v) => !TYPE_ORDER.includes(v))
        .sort((a, b) => a.localeCompare(b, 'de'));
    return [...known, ...extra];
}

function HomeContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const categoryParam = searchParams.get('category');
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState('objekte');
    const [isOverCarousel, setIsOverCarousel] = useState(false);
    const [isDesktop, setIsDesktop] = useState(true);
    const [products, setProducts] = useState([]);
    // The category the currently loaded `products` actually belong to. The
    // content area renders against this (not selectedCategory) so we never show
    // the previous category's images while the next fetch is still in flight.
    const [displayedCategory, setDisplayedCategory] = useState(null);
    const [viewMode, setViewMode] = useState('carousel');
    const [promotions, setPromotions] = useState([]);
    const [artTypes, setArtTypes] = useState(() => orderTypes(TYPE_ORDER));
    const containerRef = useRef(null);
    const fetchIdRef = useRef(0);

    const CATEGORIES = useMemo(() => {
        const art = artTypes.map((value) => {
            const id = slugify(value);
            return { id, typeValue: value, name: t(`home.categories.${id}`, value) };
        });
        return [
            ...art,
            { id: 'shop', shopOnly: true, name: t('home.categories.shop', 'Shop') },
            {
                id: 'urnen',
                externalUrl: 'https://oppermann-urnen.de',
                name: t('home.categories.urnen', 'Urnen'),
            },
        ];
    }, [artTypes, t]);

    const fetchProducts = useCallback(
        async (categoryId) => {
            const category = CATEGORIES.find((c) => c.id === categoryId);
            if (!category || category.externalUrl) return;
            // Guard against out-of-order responses when switching tabs quickly:
            // only the most recent request is allowed to commit its result.
            const requestId = ++fetchIdRef.current;
            try {
                const { products } = await listProducts(
                    category.shopOnly
                        ? { shopOnly: true, limit: 100 }
                        : { typeValue: category.typeValue, limit: 100 }
                );
                if (requestId !== fetchIdRef.current) return;
                // Commit products and the category they belong to together, so the
                // content swaps in a single render once the new images are ready.
                setProducts(products);
                setDisplayedCategory(categoryId);
            } catch (error) {
                if (requestId !== fetchIdRef.current) return;
                console.error('Error fetching products:', error);
                setProducts([]);
                setDisplayedCategory(categoryId);
            }
        },
        [CATEGORIES]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia("(min-width: 768px)");
        setIsDesktop(mediaQuery.matches);
        const handleResize = (e) => setIsDesktop(e.matches);
        mediaQuery.addListener(handleResize);
        return () => mediaQuery.removeListener(handleResize);
    }, []);

    useEffect(() => {
        let active = true;
        getProductTypes()
            .then((values) => {
                if (active && values.length) setArtTypes(orderTypes(values));
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    // Active automatic promotions, fetched once, so the shop can preview the
    // discounted price (the cart re-applies the same promotions when ordering).
    useEffect(() => {
        let active = true;
        getActivePromotions()
            .then((list) => {
                if (active) setPromotions(list);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    // A ?category= param (e.g. the navbar logo linking to objekte, or a deep
    // link from the about page) selects the tab. Reacting to the param (rather
    // than reading it once on mount) means clicking the logo while already on
    // the home page also switches back to the Objekte view. With no param, the
    // page falls back to the default `selectedCategory` (objekte), so a bare
    // visit to the site always starts on Objekte.
    useEffect(() => {
        if (!categoryParam) return;
        setSelectedCategory(categoryParam);
    }, [categoryParam]);

    useEffect(() => {
        if (!CATEGORIES.length) return;
        if (!CATEGORIES.some((c) => c.id === selectedCategory)) {
            setSelectedCategory(CATEGORIES[0].id);
        }
    }, [CATEGORIES, selectedCategory]);

    useEffect(() => {
        if (selectedCategory) fetchProducts(selectedCategory);
    }, [selectedCategory, fetchProducts]);

    useGSAP(() => {
        const mm = gsap.matchMedia();
        mm.add('(prefers-reduced-motion: no-preference)', () => {
            gsap.from(containerRef.current, {
                autoAlpha: 0,
                duration: 1,
                ease: "power2.out",
            });
        });
    }, { scope: containerRef });

    const handleCategorySelect = useCallback((categoryId) => {
        setSelectedCategory(categoryId);
        // Keep the URL in sync without a navigation/reload so the filterbar
        // stays seamless but the current tab is shareable / deep-linkable.
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('category', categoryId);
            window.history.replaceState(null, '', url);
        } catch {}
    }, []);

    const handleCursorOverCarousel = useCallback((isOver) => {
        setIsOverCarousel(isOver);
    }, []);

    const handleProductClick = useCallback((product) => {
        router.push(`/product/${product.handle}`);
    }, [router]);

    const toggleViewMode = () => {
        if (isDesktop) {
            setViewMode((prev) => (prev === 'carousel' ? 'grid' : 'carousel'));
        }
    };

    return (
        <ErrorBoundary>
            <div className="relative min-h-screen z-20 bg-gradient-to-br from-white to-gray-300 ">
                <main className="flex flex-col text-black" ref={containerRef}>
                    {isDesktop && <SmoothCursorFollower isOverCarousel={isOverCarousel}/>}
                    <Navbar
                        categories={CATEGORIES}
                        selectedCategory={selectedCategory}
                        onCategorySelect={handleCategorySelect}
                    />
                    <div className="flex-grow flex flex-col mt-12">
                        {isDesktop && (
                            <FilterBar
                                categories={CATEGORIES}
                                selectedCategory={selectedCategory}
                                onCategorySelect={handleCategorySelect}
                                viewMode={viewMode}
                                toggleViewMode={toggleViewMode}
                                isDesktop={isDesktop}
                                showViewToggle={selectedCategory !== 'shop'}
                            />
                        )}
                        <section className="relative flex-grow overflow-hidden w-full mt-4">
                            {displayedCategory === 'shop' ? (
                                <ShopView
                                    key={displayedCategory}
                                    products={products}
                                    promotions={promotions}
                                    onProductClick={handleProductClick}
                                />
                            ) : isDesktop ? (
                                viewMode === 'carousel' ? (
                                    <ImageCarousel
                                        key={displayedCategory}
                                        selectedCategory={displayedCategory}
                                        onCursorOverCarousel={handleCursorOverCarousel}
                                        products={products}
                                        promotions={promotions}
                                    />
                                ) : (
                                    <ProductGrid
                                        key={displayedCategory}
                                        products={products}
                                        handleProductClick={handleProductClick}
                                        selectedCategory={displayedCategory}
                                        promotions={promotions}
                                    />
                                )
                            ) : (
                                <ProductGrid
                                    key={displayedCategory}
                                    products={products}
                                    handleProductClick={handleProductClick}
                                    selectedCategory={displayedCategory}
                                    promotions={promotions}
                                />
                            )}
                        </section>
                    </div>
                    <Title/>
                </main>
            </div>
            <Footer/>
        </ErrorBoundary>
    );
}

export default function Home() {
    return (
        <Suspense fallback={null}>
            <HomeContent />
        </Suspense>
    );
}
